import { createConfigParametersRepository } from '../../configParametersRepository';
import {
  DATALAYER_STORAGE_KEY,
  STORAGE_KEY,
  TC_VARS,
} from '../resources/constants';
import { StorageTrackingData } from '../types';
import { Datalayer } from './types';
import { checkThirdPartyTrackingStorageInit } from '../../configParametersRepository/storageClient/storage';

/**
 * Creates a datalayer storage client for managing datalayer and session storage operations.
 * @returns {object} - An object containing methods to interact with the datalayer and session storage.
 */
export const datalayerStorageClient = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();

  /**
   * Updates the datalayer stored in the tracking storage client by merging the existing data
   * with the provided partial datalayer object.
   *
   * @param datalayer - A partial object of type `Datalayer` containing the properties to update
   * in the existing datalayer. Only the provided properties will be merged into the existing data.
   *
   * @remarks
   * Do not store product related params using this function
   */
  const updateDatalayerGlobalParams = (datalayer: Partial<Datalayer>) => {
    checkThirdPartyTrackingStorageInit();
    const { update } = getTrackingStorageClient();
    update(DATALAYER_STORAGE_KEY, (existingData = {}) => {
      return { ...existingData, ...datalayer };
    });
  };

  const getSessionStorageItem = <T>(
    key: string,
    fallbackValue: T = {} as T
  ): T => {
    checkThirdPartyTrackingStorageInit();
    const { getFromStorage } = getTrackingStorageClient();
    return getFromStorage<T>('sessionStorage', key, fallbackValue);
  };

  const setSessionStorageItem = <T>(key: string, value: T) => {
    checkThirdPartyTrackingStorageInit();
    const { setInStorage } = getTrackingStorageClient();
    setInStorage('sessionStorage', key, value);
  };

  /**
   * Retrieves the current stored datalayer.
   * @returns {Partial<Datalayer>|undefined} - The current datalayer or undefined if not available.
   */
  const getDatalayerGlobalParams = (): Partial<Datalayer> | undefined => {
    checkThirdPartyTrackingStorageInit();
    const storageTrackingData: StorageTrackingData =
      getSessionStorageItem(STORAGE_KEY);
    return storageTrackingData?.[DATALAYER_STORAGE_KEY];
  };

  /**
   * Retrieves the previous state of the datalayer by merging its historical states.
   *
   * @returns {Partial<Datalayer>} A partial representation of the datalayer,
   * combining all historical states. If no history exists, returns an empty object.
   */
  const getPreviousDatalayer = (): Partial<Datalayer> => {
    return getSessionStorageItem(TC_VARS, {}) || {};
  };

  /**
   * Pushes a new datalayer object into the datalayer history stored in session storage.
   *
   * @param datalayer - A partial representation of the `Datalayer` object to be added to the history.
   *                     This allows adding only specific properties of the `Datalayer`.
   *
   * The function retrieves the current datalayer history from session storage, appends the new
   * datalayer object to the history, and then updates the session storage with the new history.
   */
  const storePreviousDatalayer = (datalayer: Partial<Datalayer>) => {
    const previous = getPreviousDatalayer();
    setSessionStorageItem(TC_VARS, { ...previous, ...datalayer });
  };

  // TODO: get clientId directly from GA4 when UA is removed
  const getClientId = (): string => {
    checkThirdPartyTrackingStorageInit();
    const storageTrackingData: StorageTrackingData =
      getSessionStorageItem(STORAGE_KEY);
    return storageTrackingData?.userProperties?.client_id || 'not-initialized';
  };

  return {
    getDatalayerGlobalParams,
    getPreviousDatalayer,
    storePreviousDatalayer,
    updateDatalayerGlobalParams,
    getClientId,
  };
};
